import React, { useEffect, useState } from 'react';
import { Play, ArrowRight, Video as VideoIcon } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';

/**
 * FeaturedVideo — embedded YouTube player whose URL is admin-configurable
 * via `public.featured_videos` (slot keyed).  Thumbnail-first, click to load
 * the iframe — no YouTube JS until the user opts in.
 *
 * Resolution order for the URL:
 *   1. row from Supabase (admin-configured) — highest priority
 *   2. `fallbackUrl` prop                   — if admin hasn't configured yet
 *   3. `fallbackPoster` flag                — render a "video coming soon" card
 *
 * If none of those resolve, the component renders nothing.  This lets the
 * Home page show the launch video immediately (via fallbackUrl) while Benefits
 * can show a tasteful placeholder (via fallbackPoster) until the founder
 * pastes the URL in /admin → Featured Videos.
 */
export default function FeaturedVideo({
    slot,
    tone = 'dark',
    className = '',
    fallbackUrl,
    fallbackTitle,
    fallbackSubtitle,
    fallbackPoster = false,    // when true, show a styled placeholder if URL is missing
}) {
    const [row, setRow]       = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [opened, setOpened] = useState(false);

    useEffect(() => {
        let mounted = true;
        supabase
            .from('featured_videos')
            .select('youtube_url, title, subtitle, is_visible')
            .eq('slot', slot)
            .maybeSingle()
            .then(({ data }) => {
                if (!mounted) return;
                if (data && data.is_visible) setRow(data);
                setLoaded(true);
            })
            .catch(() => { if (mounted) setLoaded(true); });
        return () => { mounted = false; };
    }, [slot]);

    if (!loaded) return null;

    // Pick the URL (admin DB wins, then prop fallback)
    const resolvedUrl   = (row?.youtube_url || '').trim() || fallbackUrl;
    const title         = row?.title    || fallbackTitle    || '';
    const subtitle      = row?.subtitle || fallbackSubtitle || '';
    const ytId          = extractYouTubeId(resolvedUrl);

    // Nothing to show, and no placeholder requested → render nothing.
    if (!ytId && !fallbackPoster) return null;

    const dark = tone === 'dark';

    return (
        <section data-testid={`featured-video-${slot}`} className={`w-full ${className}`}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                {(title || subtitle) && (
                    <header className="mb-7 sm:mb-10 max-w-2xl">
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.3em] mb-4 ${
                            dark ? 'bg-indigo-400/10 border border-indigo-400/30 text-indigo-300' : 'bg-indigo-50 border border-indigo-200 text-indigo-700'
                        }`}>
                            Watch
                        </div>
                        {title && (
                            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-3 ${dark ? 'text-white' : 'text-slate-950'}`}>
                                {title}
                            </h2>
                        )}
                        {subtitle && (
                            <p className={`text-sm sm:text-base leading-relaxed font-medium ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                                {subtitle}
                            </p>
                        )}
                    </header>
                )}

                {ytId ? (
                    <VideoFrame
                        ytId={ytId}
                        title={title}
                        opened={opened}
                        onOpen={() => { setOpened(true); trackEvent('marketing', 'featured_video_play', slot); }}
                        dark={dark}
                    />
                ) : (
                    <ComingSoonCard dark={dark} />
                )}
            </div>
        </section>
    );
}

function VideoFrame({ ytId, title, opened, onOpen, dark }) {
    const thumb = `https://img.youtube.com/vi/${ytId}/maxresdefault.jpg`;
    return (
        <div className={`relative rounded-2xl sm:rounded-3xl overflow-hidden ring-1 ${dark ? 'ring-white/10' : 'ring-slate-200'} shadow-2xl ${dark ? 'shadow-indigo-900/30' : 'shadow-slate-900/10'} aspect-video`}>
            {opened ? (
                <iframe
                    src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0&modestbranding=1`}
                    title={title || 'Eager video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    loading="lazy"
                />
            ) : (
                <button
                    type="button"
                    onClick={onOpen}
                    className="absolute inset-0 group cursor-pointer focus:outline-none"
                    aria-label="Play video"
                >
                    <img
                        src={thumb}
                        alt={title || 'Eager video'}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-2xl ring-4 ring-white/20 group-hover:scale-110 group-active:scale-95 transition-transform">
                            <Play className="w-7 h-7 sm:w-8 sm:h-8 text-slate-900 fill-slate-900 ml-1" />
                        </div>
                    </div>
                    <div className="absolute bottom-3 sm:bottom-5 left-3 sm:left-5 right-3 sm:right-5 flex items-center gap-2 text-white text-[11px] sm:text-xs font-black uppercase tracking-[0.2em]">
                        <span>Play</span>
                        <ArrowRight className="w-3 h-3" />
                    </div>
                </button>
            )}
        </div>
    );
}

/**
 * ComingSoonCard — rendered when no URL is configured and no fallbackUrl is
 * provided but the parent passed `fallbackPoster`. Keeps the page balanced
 * so we don't end up with a gaping blank section.
 */
function ComingSoonCard({ dark }) {
    return (
        <div className={`relative rounded-2xl sm:rounded-3xl overflow-hidden ring-1 ${dark ? 'ring-white/10 bg-white/[0.03]' : 'ring-slate-200 bg-white'} aspect-video flex items-center justify-center`}>
            {/* Subtle backdrop pattern */}
            <div className="absolute inset-0 opacity-30">
                <div className={`absolute inset-0 ${dark ? 'bg-[radial-gradient(circle_at_30%_30%,rgba(99,102,241,0.15),transparent_40%),radial-gradient(circle_at_70%_70%,rgba(244,114,182,0.10),transparent_40%)]' : 'bg-[radial-gradient(circle_at_30%_30%,rgba(99,102,241,0.08),transparent_40%),radial-gradient(circle_at_70%_70%,rgba(251,191,36,0.08),transparent_40%)]'}`} />
            </div>
            <div className="relative text-center px-6">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${dark ? 'bg-white/10 text-white/70' : 'bg-slate-100 text-slate-500'}`}>
                    <VideoIcon className="w-7 h-7 sm:w-8 sm:h-8" />
                </div>
                <div className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.2em] mb-3 ${dark ? 'bg-amber-400/15 text-amber-300 border border-amber-400/30' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                    Video coming soon
                </div>
                <p className={`text-sm font-medium ${dark ? 'text-slate-300' : 'text-slate-500'}`}>
                    We're filming this one — drop back in a few days.
                </p>
            </div>
        </div>
    );
}

function extractYouTubeId(url) {
    if (!url) return null;
    const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
}
